import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import AIAccuracyReport from './AIAccuracyReport';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { 
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  User,
  FileCheck,
  FileX,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Eye,
  Download,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Loader2,
  RefreshCw,
  Globe,
  Video,
  Image,
  AlertCircle,
  Info,
  ChevronRight,
  Brain,
  Sparkles,
  Zap,
  TrendingUp,
  Flag,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  under_review: 'bg-blue-100 text-blue-700 border-blue-200',
  verified: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  awaiting_documents: 'bg-gray-100 text-gray-700 border-gray-200',
};

// AI Pre-approval status colors
const AI_STATUS_COLORS = {
  auto_approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  recommended_approval: 'bg-green-100 text-green-700 border-green-200',
  needs_review: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  flagged_suspicious: 'bg-red-100 text-red-700 border-red-200',
};

const AI_STATUS_LABELS = {
  auto_approved: 'AI Auto-Approved',
  recommended_approval: 'AI Recommends Approval',
  needs_review: 'Needs Review',
  flagged_suspicious: 'Flagged Suspicious',
};

// Provider type labels
const PROVIDER_TYPE_LABELS = {
  coach: 'Sports Coach',
  physiotherapist: 'Physiotherapist',
  psychologist: 'Sports Psychologist',
  nutritionist: 'Sports Nutritionist',
};

const AdminSportsVerification = () => {
  const [selectedCountry, setSelectedCountry] = useState('SL');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('queue');
  
  // Data
  const [verifications, setVerifications] = useState([]);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [countryConfig, setCountryConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [verificationSettings, setVerificationSettings] = useState(null);
  const [aiStats, setAiStats] = useState(null);
  
  // Selected verification for review
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [documentIssues, setDocumentIssues] = useState([]);
  
  // DBS Dialog (UK)
  const [dbsDialogOpen, setDbsDialogOpen] = useState(false);
  const [dbsForm, setDbsForm] = useState({
    dbs_certificate_number: '',
    dbs_issue_date: '',
    dbs_type: 'enhanced',
    update_service_id: '',
  });

  // AI Pre-approval
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResultDialogOpen, setAiResultDialogOpen] = useState(false);
  const [currentAiResult, setCurrentAiResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [verificationsRes, configRes, statsRes, settingsRes, aiStatsRes] = await Promise.all([
        axios.get(`${API_URL}/api/sports-services/admin/verifications/by-country/${selectedCountry}`),
        axios.get(`${API_URL}/api/sports-services/verification/country-config/${selectedCountry}`),
        axios.get(`${API_URL}/api/sports-services/admin/verifications/stats`),
        axios.get(`${API_URL}/api/sports-services/admin/verification-settings/${selectedCountry}`),
        axios.get(`${API_URL}/api/sports-services/admin/ai-pre-approval-stats`).catch(() => ({ data: null })),
      ]);
      
      setVerifications(verificationsRes.data.verifications || []);
      setPendingProviders(verificationsRes.data.pending_providers || []);
      setCountryConfig(configRes.data);
      setStats(statsRes.data);
      setVerificationSettings(settingsRes.data);
      setAiStats(aiStatsRes.data);
    } catch (err) {
      console.error('Failed to fetch verification data:', err);
    } finally {
      setLoading(false);
    }
  };

  const runAiPreApproval = async (verification) => {
    if (!verification.documents || verification.documents.length === 0) {
      alert('No documents available for AI analysis');
      return;
    }
    
    setAiAnalyzing(true);
    try {
      const response = await axios.post(`${API_URL}/api/sports-services/admin/ai-pre-approval`, {
        verification_ref: verification.verification_ref,
        documents: verification.documents,
        provider_name: verification.provider_details?.name || verification.provider_details?.provider_name || 'Unknown',
        provider_type: verification.provider_type,
        country: selectedCountry,
      });
      
      setCurrentAiResult(response.data.result);
      setAiResultDialogOpen(true);
      await fetchData(); // Refresh to get updated verification with AI analysis
    } catch (err) {
      console.error('AI pre-approval failed:', err);
      alert('AI analysis failed. Please try again.');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleReviewAction = async () => {
    if (!selectedVerification) return;
    
    try {
      await axios.put(
        `${API_URL}/api/sports-services/admin/verifications/${selectedVerification.verification_ref}/review`,
        null,
        {
          params: {
            action: reviewAction,
            admin_note: reviewNote,
          },
          headers: { 'Content-Type': 'application/json' },
        }
      );
      
      setReviewDialogOpen(false);
      setSelectedVerification(null);
      setReviewAction('');
      setReviewNote('');
      await fetchData();
    } catch (err) {
      console.error('Failed to process verification:', err);
      alert('Failed to process verification');
    }
  };

  const handleDBSVerification = async () => {
    if (!selectedVerification) return;
    
    try {
      await axios.post(`${API_URL}/api/sports-services/admin/dbs-check`, {
        verification_ref: selectedVerification.verification_ref,
        ...dbsForm,
      });
      
      setDbsDialogOpen(false);
      setDbsForm({
        dbs_certificate_number: '',
        dbs_issue_date: '',
        dbs_type: 'enhanced',
        update_service_id: '',
      });
      await fetchData();
    } catch (err) {
      console.error('Failed to record DBS verification:', err);
      alert('Failed to record DBS verification');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post(
        `${API_URL}/api/sports-services/admin/verification-settings/${selectedCountry}`,
        verificationSettings
      );
      alert('Settings saved successfully!');
    } catch (err) {
      console.error('Failed to save settings:', err);
      alert('Failed to save settings');
    }
  };

  const openReviewDialog = (verification, action) => {
    setSelectedVerification(verification);
    setReviewAction(action);
    setReviewDialogOpen(true);
  };

  const openDBSDialog = (verification) => {
    setSelectedVerification(verification);
    setDbsDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-sports-verification">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Provider Verification
          </h2>
          <p className="text-gray-500">Verify coaches and healthcare professionals</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-40" data-testid="country-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
              <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Trust Disclaimer Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-900">Admin Verification Guidelines</p>
            <p className="text-sm text-blue-700 mt-1">
              {countryConfig?.trust_disclaimer?.admin_facing || 
                'Review all documents carefully. Verify external credentials through official channels. Document any concerns for audit trail.'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
              <p className="text-sm text-gray-500">Pending</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-blue-600">{stats.under_review}</div>
              <p className="text-sm text-gray-500">Under Review</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-green-600">{stats.verified}</div>
              <p className="text-sm text-gray-500">Verified</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-red-600">{stats.rejected}</div>
              <p className="text-sm text-gray-500">Rejected</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-purple-600">{stats.total}</div>
              <p className="text-sm text-gray-500">Total</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Pre-Approval Stats */}
      {aiStats && (
        <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              AI Pre-Approval Performance
              <Badge className="bg-purple-100 text-purple-700 ml-2">
                <Sparkles className="h-3 w-3 mr-1" />
                GPT-4o Vision
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{aiStats.total_ai_analyzed}</div>
                <p className="text-xs text-gray-500">Documents Analyzed</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <div className="text-2xl font-bold text-emerald-600">{aiStats.by_status?.auto_approved || 0}</div>
                <p className="text-xs text-gray-500">Auto-Approved</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <div className="text-2xl font-bold text-red-600">{aiStats.by_status?.flagged_suspicious || 0}</div>
                <p className="text-xs text-gray-500">Flagged Suspicious</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <div className="text-2xl font-bold text-blue-600">{aiStats.ai_accuracy?.accuracy_percent || 0}%</div>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </div>
                <p className="text-xs text-gray-500">AI Accuracy</p>
              </div>
            </div>
            {aiStats.average_confidence > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">Average AI Confidence</span>
                  <span className="font-medium">{aiStats.average_confidence}%</span>
                </div>
                <Progress value={aiStats.average_confidence} className="h-2" />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="queue" data-testid="tab-queue">
            <Clock className="h-4 w-4 mr-1" />
            Verification Queue ({verifications.filter(v => v.status === 'pending').length + pendingProviders.length})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">
            <FileCheck className="h-4 w-4 mr-1" />
            All Verifications
          </TabsTrigger>
          <TabsTrigger value="ai-report" data-testid="tab-ai-report">
            <Brain className="h-4 w-4 mr-1" />
            AI Accuracy Report
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Globe className="h-4 w-4 mr-1" />
            Country Settings
          </TabsTrigger>
        </TabsList>

        {/* Queue Tab */}
        <TabsContent value="queue" className="mt-4 space-y-4">
          {/* Country-specific requirements info */}
          <Card className="bg-gradient-to-r from-gray-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {selectedCountry === 'UK' ? '🇬🇧 United Kingdom' : '🇱🇰 Sri Lanka'} Requirements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {selectedCountry === 'UK' ? (
                  <>
                    <div className="flex items-start gap-2">
                      <ShieldCheck className="h-4 w-4 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium">DBS Check Required</p>
                        <p className="text-gray-500">Enhanced DBS with barred list check for coaches working with children</p>
                        <a 
                          href="https://www.gov.uk/dbs-update-service" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1 mt-1"
                        >
                          Verify via DBS Update Service <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <FileCheck className="h-4 w-4 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Photo ID + Qualifications</p>
                        <p className="text-gray-500">Passport/Driving licence + UK Coaching Certificate or NGB qualification</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-2">
                      <ShieldCheck className="h-4 w-4 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Police Clearance Required</p>
                        <p className="text-gray-500">Certificate from nearest police station, less than 6 months old</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <FileCheck className="h-4 w-4 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium">NIC + Qualifications</p>
                        <p className="text-gray-500">National ID + Coaching certificate OR School Principal letter with contact</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pending Verifications */}
          {[...verifications.filter(v => v.status === 'pending'), ...pendingProviders].length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <ShieldCheck className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700">All Caught Up!</h3>
                <p className="text-gray-500">No pending verifications for {selectedCountry}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Pending verifications with documents */}
              {verifications.filter(v => v.status === 'pending' || v.status === 'under_review').map((v) => (
                <VerificationCard
                  key={v.id || v.verification_ref}
                  verification={v}
                  countryConfig={countryConfig}
                  selectedCountry={selectedCountry}
                  onApprove={() => openReviewDialog(v, 'approve')}
                  onReject={() => openReviewDialog(v, 'reject')}
                  onRequestMore={() => openReviewDialog(v, 'request_more_info')}
                  onDBSCheck={() => openDBSDialog(v)}
                  onRunAI={() => runAiPreApproval(v)}
                  aiAnalyzing={aiAnalyzing}
                />
              ))}
              
              {/* Providers awaiting documents */}
              {pendingProviders.map((p) => (
                <Card key={p.provider_id} className="border-l-4 border-l-gray-400">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                          {p.provider_details?.profile_photo ? (
                            <img 
                              src={p.provider_details.profile_photo} 
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="h-7 w-7 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{p.provider_details?.name || p.provider_details?.provider_name}</h3>
                          <p className="text-sm text-gray-500">{PROVIDER_TYPE_LABELS[p.provider_type] || p.provider_type}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                            {p.provider_details?.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {p.provider_details.city}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Badge className={STATUS_COLORS.awaiting_documents}>
                        Awaiting Documents
                      </Badge>
                    </div>
                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      <p className="text-sm text-yellow-700">
                        This provider has registered but not yet submitted verification documents.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* All Verifications Tab */}
        <TabsContent value="all" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>All Verification Records</CardTitle>
              <CardDescription>Complete verification history for {selectedCountry}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {verifications.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No verification records found</p>
                ) : (
                  verifications.map((v) => (
                    <div 
                      key={v.id || v.verification_ref}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                          {v.provider_details?.profile_photo ? (
                            <img src={v.provider_details.profile_photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{v.provider_details?.name || v.provider_details?.provider_name}</p>
                          <p className="text-xs text-gray-500">
                            {PROVIDER_TYPE_LABELS[v.provider_type]} · {v.verification_level} level
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={STATUS_COLORS[v.status] || 'bg-gray-100 text-gray-700'}>
                          {v.status}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {new Date(v.created_at).toLocaleDateString()}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedVerification(v);
                            setReviewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Accuracy Report Tab */}
        <TabsContent value="ai-report" className="mt-4">
          <AIAccuracyReport />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Verification Settings for {selectedCountry}
              </CardTitle>
              <CardDescription>
                Configure which verification checks are required for this country
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SL-specific toggles */}
              {selectedCountry === 'SL' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700">Sri Lanka Verification Checks</h4>
                  {[
                    { key: 'police_clearance', label: 'Police Clearance Certificate', desc: 'Require police clearance for all providers' },
                    { key: 'nic_verification', label: 'NIC Verification', desc: 'Verify National ID Card' },
                    { key: 'character_certificate', label: 'Character Certificate', desc: 'From Grama Niladhari or JP' },
                    { key: 'qualification_check', label: 'Qualification Check', desc: 'Verify coaching/professional qualifications' },
                    { key: 'reference_check', label: 'Reference Check', desc: 'Contact character references' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                      <Switch
                        checked={verificationSettings?.admin_toggles?.[item.key] !== false}
                        onCheckedChange={(v) => setVerificationSettings(prev => ({
                          ...prev,
                          admin_toggles: { ...prev?.admin_toggles, [item.key]: v }
                        }))}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* UK-specific toggles */}
              {selectedCountry === 'UK' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700">United Kingdom Verification Checks</h4>
                  {[
                    { key: 'dbs_verification', label: 'DBS Check Required', desc: 'Require Enhanced DBS certificate' },
                    { key: 'passport_verification', label: 'Photo ID Verification', desc: 'Verify passport or driving licence' },
                    { key: 'qualification_check', label: 'Qualification Check', desc: 'Verify UK Coaching/professional qualifications' },
                    { key: 'hcpc_verification', label: 'HCPC Registration', desc: 'For healthcare professionals' },
                    { key: 'insurance_verification', label: 'Insurance Verification', desc: 'Professional liability insurance' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                      <Switch
                        checked={verificationSettings?.admin_toggles?.[item.key] !== false}
                        onCheckedChange={(v) => setVerificationSettings(prev => ({
                          ...prev,
                          admin_toggles: { ...prev?.admin_toggles, [item.key]: v }
                        }))}
                      />
                    </div>
                  ))}
                  
                  {/* DBS Update Service Link */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-medium text-blue-900 flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4" />
                      DBS Online Verification
                    </h5>
                    <p className="text-sm text-blue-700 mt-1">
                      Verify DBS certificates online through the official DBS Update Service
                    </p>
                    <a 
                      href="https://www.gov.uk/dbs-update-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm mt-2"
                    >
                      Open DBS Update Service <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}

              {/* AI Pre-approval Toggle */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div>
                    <p className="font-medium">AI Pre-Approval (Coming Soon)</p>
                    <p className="text-sm text-gray-500">Automatically pre-approve verifications with genuine documents</p>
                  </div>
                  <Switch
                    checked={verificationSettings?.ai_pre_approval_enabled || false}
                    onCheckedChange={(v) => setVerificationSettings(prev => ({
                      ...prev,
                      ai_pre_approval_enabled: v
                    }))}
                    disabled
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveSettings}>
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reviewAction === 'approve' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
              {reviewAction === 'reject' && <XCircle className="h-5 w-5 text-red-600" />}
              {reviewAction === 'request_more_info' && <AlertTriangle className="h-5 w-5 text-yellow-600" />}
              {!reviewAction && <Eye className="h-5 w-5 text-blue-600" />}
              Verification Review
            </DialogTitle>
          </DialogHeader>
          
          {selectedVerification && (
            <div className="space-y-4">
              {/* Provider Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {selectedVerification.provider_details?.profile_photo ? (
                    <img 
                      src={selectedVerification.provider_details.profile_photo} 
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    {selectedVerification.provider_details?.name || selectedVerification.provider_details?.provider_name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {PROVIDER_TYPE_LABELS[selectedVerification.provider_type]} · 
                    {selectedVerification.verification_level} level
                  </p>
                  <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                    {selectedVerification.provider_details?.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {selectedVerification.provider_details.email}
                      </span>
                    )}
                    {selectedVerification.provider_details?.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {selectedVerification.provider_details.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Documents List */}
              <div>
                <h4 className="font-medium mb-2">Submitted Documents</h4>
                <div className="space-y-2">
                  {selectedVerification.documents?.map((doc, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 border rounded-lg flex items-center justify-between ${
                        doc.is_verified === true ? 'bg-green-50 border-green-200' :
                        doc.is_verified === false ? 'bg-red-50 border-red-200' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {doc.is_verified === true ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : doc.is_verified === false ? (
                          <XCircle className="h-5 w-5 text-red-600" />
                        ) : (
                          <FileCheck className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium">{doc.document_name || doc.document_type}</p>
                          {doc.issuer && <p className="text-xs text-gray-500">Issuer: {doc.issuer}</p>}
                          {doc.issue_date && <p className="text-xs text-gray-500">Issued: {doc.issue_date}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.document_url && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={doc.document_url} target="_blank" rel="noopener noreferrer">
                              <Eye className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  )) || (
                    <p className="text-gray-500 text-sm">No documents submitted yet</p>
                  )}
                </div>
              </div>

              {/* DBS Info for UK */}
              {selectedCountry === 'UK' && selectedVerification.dbs_verification && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" />
                    DBS Verification Status
                  </h4>
                  <div className="mt-2 text-sm space-y-1">
                    <p><strong>Certificate #:</strong> {selectedVerification.dbs_verification.certificate_number}</p>
                    <p><strong>Type:</strong> {selectedVerification.dbs_verification.dbs_type}</p>
                    <p><strong>Verified:</strong> {new Date(selectedVerification.dbs_verification.verified_at).toLocaleDateString()}</p>
                  </div>
                </div>
              )}

              {/* References */}
              {selectedVerification.references?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Character References</h4>
                  <div className="space-y-2">
                    {selectedVerification.references.map((ref, idx) => (
                      <div key={idx} className="p-3 border rounded-lg">
                        <p className="font-medium">{ref.name}</p>
                        <p className="text-sm text-gray-500">{ref.relationship}</p>
                        <p className="text-sm text-gray-500">{ref.phone}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Note */}
              {reviewAction && (
                <div>
                  <Label>Admin Notes</Label>
                  <Textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder={
                      reviewAction === 'approve' ? 'Optional: Add verification notes...' :
                      reviewAction === 'reject' ? 'Required: Explain rejection reason...' :
                      'Specify what additional information is needed...'
                    }
                    className="mt-1"
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {selectedCountry === 'UK' && selectedVerification && !selectedVerification.dbs_verification && (
              <Button variant="outline" onClick={() => {
                setReviewDialogOpen(false);
                openDBSDialog(selectedVerification);
              }}>
                <ShieldCheck className="h-4 w-4 mr-1" />
                Record DBS
              </Button>
            )}
            {reviewAction === 'approve' && (
              <Button onClick={handleReviewAction} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Approve
              </Button>
            )}
            {reviewAction === 'reject' && (
              <Button onClick={handleReviewAction} variant="destructive">
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
            )}
            {reviewAction === 'request_more_info' && (
              <Button onClick={handleReviewAction} className="bg-yellow-600 hover:bg-yellow-700">
                <AlertTriangle className="h-4 w-4 mr-1" />
                Request More Info
              </Button>
            )}
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DBS Verification Dialog */}
      <Dialog open={dbsDialogOpen} onOpenChange={setDbsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              Record DBS Verification
            </DialogTitle>
            <DialogDescription>
              Enter the DBS certificate details after verifying through the DBS Update Service
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>DBS Certificate Number</Label>
              <Input
                value={dbsForm.dbs_certificate_number}
                onChange={(e) => setDbsForm(prev => ({ ...prev, dbs_certificate_number: e.target.value }))}
                placeholder="e.g., 001234567890"
              />
            </div>
            
            <div>
              <Label>Issue Date</Label>
              <Input
                type="date"
                value={dbsForm.dbs_issue_date}
                onChange={(e) => setDbsForm(prev => ({ ...prev, dbs_issue_date: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>DBS Type</Label>
              <Select 
                value={dbsForm.dbs_type} 
                onValueChange={(v) => setDbsForm(prev => ({ ...prev, dbs_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="enhanced">Enhanced</SelectItem>
                  <SelectItem value="enhanced_with_barred">Enhanced with Barred List</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Update Service ID (Optional)</Label>
              <Input
                value={dbsForm.update_service_id}
                onChange={(e) => setDbsForm(prev => ({ ...prev, update_service_id: e.target.value }))}
                placeholder="If registered with Update Service"
              />
              <p className="text-xs text-gray-500 mt-1">
                For continuous DBS monitoring
              </p>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg">
              <a 
                href="https://www.gov.uk/dbs-update-service"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"
              >
                <ExternalLink className="h-3 w-3" />
                Open DBS Update Service to verify
              </a>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDbsDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDBSVerification}
              disabled={!dbsForm.dbs_certificate_number || !dbsForm.dbs_issue_date}
            >
              Save DBS Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Pre-Approval Result Dialog */}
      <Dialog open={aiResultDialogOpen} onOpenChange={setAiResultDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              AI Pre-Approval Analysis
              <Badge className="bg-purple-100 text-purple-700 ml-2">
                <Sparkles className="h-3 w-3 mr-1" />
                GPT-4o Vision
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {currentAiResult && (
            <div className="space-y-4">
              {/* Overall Result */}
              <div className={`p-4 rounded-lg border-2 ${
                currentAiResult.pre_approval_status === 'auto_approved' ? 'bg-emerald-50 border-emerald-300' :
                currentAiResult.pre_approval_status === 'recommended_approval' ? 'bg-green-50 border-green-300' :
                currentAiResult.pre_approval_status === 'needs_review' ? 'bg-yellow-50 border-yellow-300' :
                'bg-red-50 border-red-300'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      {currentAiResult.pre_approval_status === 'auto_approved' && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                      {currentAiResult.pre_approval_status === 'recommended_approval' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                      {currentAiResult.pre_approval_status === 'needs_review' && <AlertTriangle className="h-5 w-5 text-yellow-600" />}
                      {currentAiResult.pre_approval_status === 'flagged_suspicious' && <ShieldX className="h-5 w-5 text-red-600" />}
                      {AI_STATUS_LABELS[currentAiResult.pre_approval_status]}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{currentAiResult.summary}</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">{currentAiResult.overall_confidence}%</div>
                    <p className="text-xs text-gray-500">Confidence</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Badge variant="outline">{currentAiResult.estimated_review_time} review</Badge>
                  <span className="text-sm text-gray-600">{currentAiResult.action_required}</span>
                </div>
              </div>

              {/* Document Analysis */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileCheck className="h-4 w-4" />
                  Document Analysis
                </h4>
                <div className="space-y-3">
                  {currentAiResult.document_analyses?.map((doc, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 border rounded-lg ${
                        doc.risk_level === 'low' ? 'bg-green-50 border-green-200' :
                        doc.risk_level === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                        doc.risk_level === 'high' ? 'bg-red-50 border-red-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {doc.is_authentic ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-yellow-600" />
                          )}
                          <div>
                            <p className="font-medium">{doc.document_name || doc.document_type}</p>
                            <p className="text-xs text-gray-500">Risk: {doc.risk_level}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-semibold">{doc.confidence_score}%</div>
                          <Progress value={doc.confidence_score} className="w-24 h-1.5" />
                        </div>
                      </div>
                      
                      {doc.analysis_notes && (
                        <p className="text-sm text-gray-600 mt-2">{doc.analysis_notes}</p>
                      )}
                      
                      {doc.flags && doc.flags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {doc.flags.map((flag, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                              <Flag className="h-2.5 w-2.5 mr-1" />
                              {flag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {doc.recommendations && (
                        <div className="mt-2 p-2 bg-white rounded text-xs">
                          <span className="font-medium">Recommendation:</span> {doc.recommendations}
                        </div>
                      )}
                      
                      {doc.metadata_extracted && Object.keys(doc.metadata_extracted).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-gray-500 cursor-pointer">Extracted Data</summary>
                          <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                            {JSON.stringify(doc.metadata_extracted, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Cross-validation */}
              {currentAiResult.cross_validation && (
                <div className={`p-3 rounded-lg ${
                  currentAiResult.cross_validation.consistent ? 'bg-green-50' : 'bg-yellow-50'
                }`}>
                  <h4 className="font-medium flex items-center gap-2">
                    {currentAiResult.cross_validation.consistent ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    )}
                    Cross-Document Validation
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">{currentAiResult.cross_validation.notes}</p>
                  {currentAiResult.cross_validation.inconsistencies?.length > 0 && (
                    <ul className="text-sm text-red-600 mt-2 list-disc list-inside">
                      {currentAiResult.cross_validation.inconsistencies.map((issue, i) => (
                        <li key={i}>{issue}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="text-xs text-gray-400 text-right">
                Analyzed at: {new Date(currentAiResult.analyzed_at).toLocaleString()} using {currentAiResult.ai_model_used}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiResultDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Verification Card Component
const VerificationCard = ({ 
  verification, 
  countryConfig, 
  selectedCountry,
  onApprove, 
  onReject, 
  onRequestMore,
  onDBSCheck,
  onRunAI,
  aiAnalyzing
}) => {
  const provider = verification.provider_details;
  
  return (
    <Card className={`border-l-4 ${
      verification.status === 'pending' ? 'border-l-yellow-500' :
      verification.status === 'under_review' ? 'border-l-blue-500' :
      'border-l-gray-300'
    }`}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          {/* Provider Info */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {provider?.profile_photo ? (
                <img 
                  src={provider.profile_photo} 
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="h-8 w-8 text-gray-400" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-lg">
                {provider?.name || provider?.provider_name || 'Unknown Provider'}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline">
                  {PROVIDER_TYPE_LABELS[verification.provider_type] || verification.provider_type}
                </Badge>
                <Badge variant="secondary">
                  {verification.verification_level} level
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
                {provider?.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {provider.city}
                  </span>
                )}
                {provider?.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {provider.email}
                  </span>
                )}
                {provider?.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {provider.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Status Badge */}
          <Badge className={STATUS_COLORS[verification.status] || 'bg-gray-100'}>
            {verification.status.replace('_', ' ')}
          </Badge>
        </div>

        {/* Documents Summary */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-sm mb-2">Submitted Documents ({verification.documents?.length || 0})</h4>
          <div className="flex flex-wrap gap-2">
            {verification.documents?.map((doc, idx) => (
              <Badge 
                key={idx} 
                variant={doc.is_verified === true ? 'default' : doc.is_verified === false ? 'destructive' : 'secondary'}
                className="text-xs"
              >
                {doc.is_verified === true && <CheckCircle2 className="h-3 w-3 mr-1" />}
                {doc.is_verified === false && <XCircle className="h-3 w-3 mr-1" />}
                {doc.document_name || doc.document_type}
              </Badge>
            )) || <span className="text-gray-500 text-sm">No documents</span>}
          </div>
        </div>

        {/* AI Pre-Approval Status */}
        {verification.ai_analysis ? (
          <div className={`mt-3 p-3 rounded-lg ${
            verification.ai_pre_approval_status === 'auto_approved' ? 'bg-emerald-50 border border-emerald-200' :
            verification.ai_pre_approval_status === 'recommended_approval' ? 'bg-green-50 border border-green-200' :
            verification.ai_pre_approval_status === 'needs_review' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium">
                  {AI_STATUS_LABELS[verification.ai_pre_approval_status]}
                </span>
                <Badge variant="outline" className="text-xs">
                  {verification.ai_confidence}% confidence
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {verification.ai_analysis.document_analyses?.some(d => d.flags?.length > 0) && (
                  <Badge className="bg-red-100 text-red-700 text-xs">
                    <Flag className="h-3 w-3 mr-1" />
                    {verification.ai_analysis.document_analyses.reduce((sum, d) => sum + (d.flags?.length || 0), 0)} flags
                  </Badge>
                )}
                <Badge className="bg-purple-100 text-purple-700 text-xs">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI Analyzed
                </Badge>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">{verification.ai_analysis.action_required}</p>
          </div>
        ) : verification.documents?.length > 0 ? (
          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-purple-600" />
                <span className="text-sm text-purple-700">AI Pre-Approval Available</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onRunAI}
                disabled={aiAnalyzing}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                {aiAnalyzing ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Zap className="h-3 w-3 mr-1" />
                    Run AI Analysis
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : null}

        {/* DBS Status for UK */}
        {selectedCountry === 'UK' && (
          <div className={`mt-3 p-3 rounded-lg ${
            verification.dbs_verification ? 'bg-green-50' : 'bg-yellow-50'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {verification.dbs_verification ? (
                  <>
                    <ShieldCheck className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-700">
                      DBS Verified: {verification.dbs_verification.dbs_type}
                    </span>
                  </>
                ) : (
                  <>
                    <ShieldAlert className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm text-yellow-700">
                      DBS Check Required
                    </span>
                  </>
                )}
              </div>
              {!verification.dbs_verification && (
                <Button variant="outline" size="sm" onClick={onDBSCheck}>
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Record DBS
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-4 pt-4 border-t flex justify-between">
          <div>
            {verification.documents?.length > 0 && !verification.ai_analysis && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onRunAI}
                disabled={aiAnalyzing}
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              >
                {aiAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Brain className="h-4 w-4 mr-1" />
                    AI Pre-Approve
                  </>
                )}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRequestMore}>
              <AlertTriangle className="h-4 w-4 mr-1" />
              Request More Info
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={onReject}>
              <XCircle className="h-4 w-4 mr-1" />
              Reject
            </Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={onApprove}>
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSportsVerification;
